import { withAlpha, hex, type Color } from "../../common/color.js";
import { clamp, type Size } from "../../common/geometry.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { RenderContext, TextSpan } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";
import { ScrollableBase, type ScrollableOptions } from "../controls/ScrollableBase.js";

export interface ChatChannel {
  id: string;
  name: string;
  color: Color;
}

export interface ChatMessage {
  channel: string;
  /** Who said it. Omitted for system lines. */
  sender?: string;
  /** Plain text, or spans for an item link inside the line. */
  body: string | readonly TextSpan[];
  /** Epoch milliseconds. Supplied by the caller, so the log has no clock. */
  at?: number;
}

export interface ChatLogOptions extends ScrollableOptions {
  channels?: readonly ChatChannel[];
  /** Only these channel ids are shown. Empty shows all of them. */
  filter?: readonly string[];
  /** Oldest lines are dropped past this many. */
  maxMessages?: number;
  showTimestamps?: boolean;
  /** Fade the whole log out after this long with nothing new. 0 never fades. */
  idleFadeSeconds?: number;
  font?: FontSpec | null;
  padding?: number;
}

const DEFAULT_MAX = 500;
const DEFAULT_PADDING = 6;
const FADE_BAND_SECONDS = 1.5;
/** How faint an idle log goes, rather than vanishing outright. */
const IDLE_ALPHA = 0.25;

/** The channels most games have, in colors players expect. */
export const DEFAULT_CHANNELS: readonly ChatChannel[] = [
  { id: "say", name: "Say", color: hex("#ffffff") },
  { id: "party", name: "Party", color: hex("#8ac6ff") },
  { id: "guild", name: "Guild", color: hex("#63d68a") },
  { id: "whisper", name: "Whisper", color: hex("#f08ce0") },
  { id: "system", name: "System", color: hex("#ffd479") },
  { id: "combat", name: "Combat", color: hex("#ff9c6b") },
];

/**
 * A scrolling chat and combat log.
 *
 * Three things make this its own control rather than a `ListBox` of strings:
 *
 * - Lines **wrap**, so a row's height depends on the width, and a list of
 *   fixed-height rows cannot represent that.
 * - Lines are **styled per channel and per run**, which is what makes an item
 *   link inside a sentence possible. That is the rich-text path.
 * - It **sticks to the bottom** — new messages scroll into view only while
 *   the reader is already at the end. Scrolling up to read something and
 *   having the log yank you back down is the single worst thing a chat window
 *   can do.
 */
export class ChatLog extends ScrollableBase {
  #messages: ChatMessage[] = [];
  #channels: Map<string, ChatChannel>;
  #filter: Set<string>;
  /** Wrapped lines, rebuilt when the width, filter or contents change. */
  #lines: TextSpan[][] = [];
  #layoutWidth = -1;
  #lineHeight = 0;
  #dirtyLines = true;
  /** Frame time of the last message, for the idle fade. */
  #lastMessageAt = 0;
  #animating = false;
  /**
   * Follow the newest message.
   *
   * On until the reader scrolls away, and on again the moment they scroll
   * back — which is the whole rule, stated once. It is a *flag* rather than a
   * measurement taken at `add` time, and that is the point: when a message
   * arrives the wrapped lines have not been rebuilt yet, so the scroll extent
   * still describes the previous contents and asking "are we at the bottom?"
   * gets an answer about the wrong text. The flag is applied after the
   * rebuild, when the extent is finally right.
   */
  #pinned = true;
  /** Set while this control is doing the scrolling, so it is not read as the reader doing it. */
  #pinning = false;

  maxMessages: number;
  showTimestamps: boolean;
  idleFadeSeconds: number;
  font: FontSpec | null;
  padding: number;

  constructor(options: ChatLogOptions = {}) {
    super({ width: 380, height: 180, ...options });
    this.#channels = new Map((options.channels ?? DEFAULT_CHANNELS).map((c) => [c.id, c]));
    this.#filter = new Set(options.filter ?? []);
    this.maxMessages = Math.max(1, options.maxMessages ?? DEFAULT_MAX);
    this.showTimestamps = options.showTimestamps ?? false;
    this.idleFadeSeconds = options.idleFadeSeconds ?? 0;
    this.font = options.font ?? null;
    this.padding = options.padding ?? DEFAULT_PADDING;
  }

  get messages(): readonly ChatMessage[] {
    return this.#messages;
  }

  get channels(): readonly ChatChannel[] {
    return [...this.#channels.values()];
  }

  /** Channel ids currently shown. Empty means all of them. */
  get filter(): readonly string[] {
    return [...this.#filter];
  }
  set filter(ids: readonly string[]) {
    this.#filter = new Set(ids);
    this.#dirtyLines = true;
    this.invalidate();
  }

  /** True when a message on `channel` would be shown. */
  shows(channel: string): boolean {
    return this.#filter.size === 0 || this.#filter.has(channel);
  }

  addChannel(channel: ChatChannel): void {
    this.#channels.set(channel.id, channel);
    this.#dirtyLines = true;
    this.invalidate();
  }

  /** True when the view is scrolled to the newest message. */
  get atBottom(): boolean {
    // Within a couple of pixels of the end counts: a scroll bar left a pixel
    // short must not stop new messages from following.
    return this.scrollOffsetY >= this.vScrollBar.maxValue - 2;
  }

  /**
   * Whether the log is following new messages.
   *
   * Set it false to hold position, or true to jump to the end and stay there.
   */
  get following(): boolean {
    return this.#pinned;
  }
  set following(v: boolean) {
    this.#pinned = v;
    if (v) this.#pinToBottom();
  }

  /**
   * A scroll that came from the reader turns following on or off; one this
   * control did itself is not a decision and must not.
   */
  protected override onScrollChanged(): void {
    super.onScrollChanged();
    if (!this.#pinning) this.#pinned = this.atBottom;
  }

  /** Glue the view to the end without that counting as the reader scrolling. */
  #pinToBottom(): void {
    this.#pinning = true;
    try {
      this.scrollOffsetY = this.vScrollBar.maxValue;
    } finally {
      this.#pinning = false;
    }
  }

  /** Post a message. Returns it, so a caller can hold on to it. */
  add(message: ChatMessage): ChatMessage {
    this.#messages.push(message);
    while (this.#messages.length > this.maxMessages) this.#messages.shift();
    this.#dirtyLines = true;
    this.#lastMessageAt = this.desktop?.frameTime ?? 0;
    this.#syncAnimation();
    // Deliberately does not scroll here. The lines have not been re-wrapped
    // yet, so the extent to scroll to is not known until the next paint.
    this.invalidate();
    return message;
  }

  /** Shorthand for a plain line on one channel. */
  say(channel: string, body: string, sender?: string): ChatMessage {
    return this.add({ channel, body, sender });
  }

  clear(): void {
    this.#messages.length = 0;
    this.#dirtyLines = true;
    // An empty log has nothing to be scrolled away from.
    this.#pinned = true;
    this.invalidate();
  }

  /** Jump to the newest message and follow it from here on. */
  scrollToBottom(): void {
    this.#pinned = true;
    this.updateScrollBars();
    this.#pinToBottom();
  }

  // --- idle fade -----------------------------------------------------------

  /** How visible the log is, 0..1. Fades when nothing has been said. */
  get opacity(): number {
    if (this.idleFadeSeconds <= 0 || this.hovered) return 1;
    const idle = (this.desktop?.frameTime ?? 0) - this.#lastMessageAt;
    if (idle <= this.idleFadeSeconds) return 1;
    const t = clamp((idle - this.idleFadeSeconds) / FADE_BAND_SECONDS, 0, 1);
    return 1 - t * (1 - IDLE_ALPHA);
  }

  #syncAnimation(): void {
    const desktop = this.desktop;
    if (!desktop) {
      this.#animating = false;
      return;
    }
    const want = this.idleFadeSeconds > 0;
    if (want === this.#animating) return;
    this.#animating = want;
    if (want) desktop.addAnimator(this);
    else desktop.removeAnimator(this);
  }

  protected override onEnvironmentChanged(): void {
    this.#animating = false;
    this.#syncAnimation();
  }

  // --- layout --------------------------------------------------------------

  /** Turn one message into the spans of a single unwrapped line. */
  #spansFor(message: ChatMessage, theme: Theme): TextSpan[] {
    const channel = this.#channels.get(message.channel);
    const ink = channel?.color ?? theme.palette.text;
    const spans: TextSpan[] = [];

    if (this.showTimestamps && message.at !== undefined) {
      spans.push({ text: `${formatClock(message.at)} `, color: theme.palette.textSubtle });
    }
    if (message.sender) {
      spans.push({ text: `[${message.sender}] `, color: ink, font: theme.metrics.captionFont });
    }
    if (typeof message.body === "string") spans.push({ text: message.body, color: ink });
    // Spans supplied by the caller keep their own colors; anything without
    // one takes the channel's, so an item link stays gold in a green line.
    else for (const span of message.body) spans.push({ color: ink, ...span });
    return spans;
  }

  #rebuild(rc: RenderContext, theme: Theme, width: number): void {
    const font = this.font ?? theme.metrics.font;
    this.#lines = [];
    for (const message of this.#messages) {
      if (!this.shows(message.channel)) continue;
      // Wrapped per message, not over the whole log: each one is its own
      // paragraph, and a message must never share a line with the next.
      this.#lines.push(...rc.wrapSpans(this.#spansFor(message, theme), font, width));
    }
    this.#layoutWidth = width;
    this.#lineHeight = rc.lineHeight(font);
    this.#dirtyLines = false;
    this.updateScrollBars();
  }

  protected override contentSize(): Size {
    return {
      width: Math.max(0, this.#layoutWidth),
      height: this.#lines.length * this.#lineHeight,
    };
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const state = this.visualState();
    theme.drawListBackground(rc, this.localRect, state);

    const view = this.viewportRect;
    if (view.width <= 0 || view.height <= 0) return;

    const font = this.font ?? theme.metrics.font;
    const width = Math.max(20, view.width - this.padding * 2);
    if (this.#dirtyLines || width !== this.#layoutWidth) this.#rebuild(rc, theme, width);
    // Applied here, once the rebuild has settled the extent — every frame, not
    // only after a rebuild, because the extent also moves when the control is
    // resized or a scroll bar appears.
    if (this.#pinned) this.#pinToBottom();
    this.#syncAnimation();

    const alpha = this.opacity;
    if (alpha <= 0) return;
    const step = this.#lineHeight;
    if (step <= 0) return;
    const ink = withAlpha(theme.palette.text, alpha);

    rc.clipped(view, () => {
      // Only the lines actually on screen. A log holding five hundred
      // messages must cost the same to draw as one holding five.
      const first = Math.max(0, Math.floor(this.scrollOffsetY / step));
      const last = Math.min(
        this.#lines.length,
        Math.ceil((this.scrollOffsetY + view.height) / step) + 1,
      );
      for (let i = first; i < last; i++) {
        const line = this.#lines[i];
        if (!line) continue;
        const y = view.y + i * step - this.scrollOffsetY;
        rc.drawSpans(
          alpha >= 1
            ? line
            : // Faded: every span's own color has to come down with the log,
              // not just the fallback, or an item link would stay bright over
              // gray text.
              line.map((span) =>
                span.color ? { ...span, color: withAlpha(span.color, alpha) } : span,
              ),
          view.x + this.padding,
          y,
          font,
          ink,
          step,
        );
      }
    });
  }
}

/** "14:32", from epoch milliseconds. */
export function formatClock(at: number): string {
  const d = new Date(at);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}
